import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  ClipboardList, Loader2, Search, Filter, Calendar, Clock,
  Cake, CalendarCheck, UserX, AlertTriangle, Eye, CheckCircle, XCircle,
  FileText, Download, Trash2
} from "lucide-react";

const TIPOS_MOVIMIENTO = {
  permiso: { label: "Permiso", icon: CalendarCheck, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  cumpleanos: { label: "Cumpleaños", icon: Cake, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
  tardanza: { label: "Tardanza", icon: Clock, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  ausencia: { label: "Ausencia", icon: UserX, color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" }
};

const SUBTIPOS = {
  general: "General",
  medico: "Médico",
  personal: "Personal",
  dia_cumpleanos: "Día de Cumpleaños",
  ausencia_justificada: "Ausencia Justificada",
  ausencia_injustificada: "Ausencia Injustificada"
};

const ESTADOS = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
  registrado: { label: "Registrado", color: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300" }
};

export default function MovimientosGHPage() {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  const [filterEstado, setFilterEstado] = useState("all");
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  
  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());
  const canDeleteMovimientos = isAdmin || user?.can_delete_movimientos_gh;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterTipo !== "all") params.append("tipo", filterTipo);
      if (filterEstado !== "all") params.append("estado", filterEstado);
      
      const res = await axios.get(`${API}/gestion-humana/movimientos?${params}`, getAuthHeaders());
      setMovimientos(res.data);
    } catch (err) {
      console.error("Error loading movimientos:", err);
      toast.error("Error al cargar movimientos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterTipo, filterEstado]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTipoInfo = (tipo) => {
    return TIPOS_MOVIMIENTO[tipo] || TIPOS_MOVIMIENTO.permiso;
  };

  const getEstadoInfo = (estado) => {
    return ESTADOS[estado] || ESTADOS.pendiente;
  };

  // Selection functions for bulk delete
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredMovimientos.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMovimientos.map(m => m.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!deletePassword || selectedIds.length === 0) {
      toast.error("Ingrese su contraseña para confirmar");
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/gestion-humana/movimientos/bulk-delete`,
        {
          ids: selectedIds,
          password: deletePassword
        },
        getAuthHeaders()
      );
      
      toast.success(`${res.data.deleted || selectedIds.length} movimientos eliminados`);
      setSelectedIds([]);
      setShowDeleteDialog(false);
      setDeletePassword("");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al eliminar movimientos";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Filter by search
  const filteredMovimientos = movimientos.filter(m => {
    const matchSearch = searchQuery === "" || 
      m.empleado_nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.motivo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.departamento?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  // Statistics
  const stats = {
    total: movimientos.length,
    permisos: movimientos.filter(m => m.tipo === "permiso").length,
    cumpleanos: movimientos.filter(m => m.tipo === "cumpleanos").length,
    tardanzas: movimientos.filter(m => m.tipo === "tardanza").length,
    ausencias: movimientos.filter(m => m.tipo === "ausencia").length,
    pendientes: movimientos.filter(m => m.estado === "pendiente").length
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto" data-testid="movimientos-gh-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
              <ClipboardList className="h-7 w-7 text-blue-600" />
              Movimientos de Gestión Humana
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Historial unificado de permisos, ausencias, tardanzas y días de cumpleaños
            </p>
          </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.permisos}</p>
              <p className="text-xs text-slate-500">Permisos</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.cumpleanos}</p>
              <p className="text-xs text-slate-500">Cumpleaños</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.tardanzas}</p>
              <p className="text-xs text-slate-500">Tardanzas</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.ausencias}</p>
              <p className="text-xs text-slate-500">Ausencias</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-500">{stats.pendientes}</p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por empleado, departamento o motivo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </div>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger className="w-[150px] dark:bg-slate-700 dark:border-slate-600">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="permiso">Permisos</SelectItem>
                  <SelectItem value="cumpleanos">Cumpleaños</SelectItem>
                  <SelectItem value="tardanza">Tardanzas</SelectItem>
                  <SelectItem value="ausencia">Ausencias</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger className="w-[150px] dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="aprobada">Aprobada</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                  <SelectItem value="registrado">Registrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Movimientos List */}
        <Card className="dark:bg-slate-800 dark:border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Historial de Movimientos ({filteredMovimientos.length})
            </CardTitle>
            {canDeleteMovimientos && filteredMovimientos.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 mr-4">
                  <Checkbox
                    id="select-all"
                    checked={selectedIds.length === filteredMovimientos.length && filteredMovimientos.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <Label htmlFor="select-all" className="text-sm cursor-pointer">
                    {selectedIds.length === filteredMovimientos.length ? "Deseleccionar" : "Seleccionar"} todo
                  </Label>
                </div>
                {selectedIds.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar ({selectedIds.length})
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {filteredMovimientos.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay movimientos registrados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMovimientos.map((mov) => {
                  const tipoInfo = getTipoInfo(mov.tipo);
                  const estadoInfo = getEstadoInfo(mov.estado);
                  const TipoIcon = tipoInfo.icon;
                  const isSelected = selectedIds.includes(mov.id);
                  
                  return (
                    <div
                      key={mov.id}
                      className={`flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        {canDeleteMovimientos && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(mov.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div 
                          className={`p-2 rounded-lg ${tipoInfo.color} cursor-pointer`}
                          onClick={() => { setSelectedMovimiento(mov); setShowDetailDialog(true); }}
                        >
                          <TipoIcon className="h-5 w-5" />
                        </div>
                        <div 
                          className="cursor-pointer flex-1"
                          onClick={() => { setSelectedMovimiento(mov); setShowDetailDialog(true); }}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800 dark:text-white">
                              {mov.empleado_nombre}
                            </span>
                            <Badge className={tipoInfo.color}>{tipoInfo.label}</Badge>
                            {mov.subtipo && SUBTIPOS[mov.subtipo] && (
                              <Badge variant="outline" className="text-xs">
                                {SUBTIPOS[mov.subtipo]}
                              </Badge>
                            )}
                            <Badge className={estadoInfo.color}>{estadoInfo.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {mov.departamento && `${mov.departamento} • `}
                            {mov.tipo === "tardanza" 
                              ? `${mov.fecha_inicio} - ${mov.minutos_tarde || 0} min tarde`
                              : mov.fecha_inicio === mov.fecha_fin 
                                ? mov.fecha_inicio 
                                : `${mov.fecha_inicio} al ${mov.fecha_fin}`
                            }
                            {mov.motivo && ` • ${mov.motivo.substring(0, 40)}${mov.motivo.length > 40 ? '...' : ''}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedMovimiento(mov); setShowDetailDialog(true); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Detalle del Movimiento
              </DialogTitle>
            </DialogHeader>
            
            {selectedMovimiento && (
              <div className="space-y-4 py-4">
                {/* Header con tipo y estado */}
                <div className="flex items-center gap-3">
                  {(() => {
                    const tipoInfo = getTipoInfo(selectedMovimiento.tipo);
                    const TipoIcon = tipoInfo.icon;
                    return (
                      <>
                        <div className={`p-2 rounded-lg ${tipoInfo.color}`}>
                          <TipoIcon className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{tipoInfo.label}</h3>
                          <div className="flex gap-2">
                            {selectedMovimiento.subtipo && SUBTIPOS[selectedMovimiento.subtipo] && (
                              <Badge variant="outline">{SUBTIPOS[selectedMovimiento.subtipo]}</Badge>
                            )}
                            <Badge className={getEstadoInfo(selectedMovimiento.estado).color}>
                              {getEstadoInfo(selectedMovimiento.estado).label}
                            </Badge>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Información del empleado */}
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Información del Empleado</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Empleado:</span>
                      <p className="font-medium">{selectedMovimiento.empleado_nombre}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Departamento:</span>
                      <p className="font-medium">{selectedMovimiento.departamento || "N/A"}</p>
                    </div>
                    {selectedMovimiento.supervisor_nombre && (
                      <div>
                        <span className="text-slate-500">Supervisor:</span>
                        <p className="font-medium">{selectedMovimiento.supervisor_nombre}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Detalles según tipo de movimiento */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-medium text-sm text-blue-600 dark:text-blue-400 mb-2">Detalles de la Solicitud</h4>
                  
                  {selectedMovimiento.tipo === "tardanza" ? (
                    // Tardanza
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Fecha:</span>
                        <p className="font-medium">{selectedMovimiento.fecha_inicio}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Hora de Entrada:</span>
                        <p className="font-medium">{selectedMovimiento.hora_entrada || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Hora Esperada:</span>
                        <p className="font-medium">{selectedMovimiento.hora_esperada || "08:00"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Minutos Tarde:</span>
                        <p className="font-medium text-amber-600">{selectedMovimiento.minutos_tarde || 0} minutos</p>
                      </div>
                    </div>
                  ) : selectedMovimiento.tipo === "permiso" ? (
                    // Permiso
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Fecha Inicio:</span>
                          <p className="font-medium">{selectedMovimiento.fecha_inicio}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Fecha Fin:</span>
                          <p className="font-medium">{selectedMovimiento.fecha_fin}</p>
                        </div>
                      </div>
                      
                      {/* Horas (para permisos por horas) */}
                      {(selectedMovimiento.hora_inicio || selectedMovimiento.hora_fin) && (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Hora Inicio:</span>
                            <p className="font-medium">{selectedMovimiento.hora_inicio || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Hora Fin:</span>
                            <p className="font-medium">{selectedMovimiento.hora_fin || "N/A"}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedMovimiento.dias > 0 && (
                          <div>
                            <span className="text-slate-500">Días Solicitados:</span>
                            <p className="font-medium">{selectedMovimiento.dias}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-slate-500">Requiere Compensación:</span>
                          <p className="font-medium">
                            {selectedMovimiento.requiere_compensacion ? (
                              <Badge className="bg-amber-100 text-amber-700">Sí</Badge>
                            ) : (
                              <Badge variant="outline">No</Badge>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : selectedMovimiento.tipo === "cumpleanos" ? (
                    // Cumpleaños
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Fecha Solicitada:</span>
                        <p className="font-medium">{selectedMovimiento.fecha_inicio}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Días:</span>
                        <p className="font-medium">1 día</p>
                      </div>
                    </div>
                  ) : (
                    // Ausencia u otro
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Fecha:</span>
                        <p className="font-medium">{selectedMovimiento.fecha_inicio}</p>
                      </div>
                      {selectedMovimiento.dias > 0 && (
                        <div>
                          <span className="text-slate-500">Días:</span>
                          <p className="font-medium">{selectedMovimiento.dias}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Motivo */}
                {selectedMovimiento.motivo && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <h4 className="font-medium text-sm text-slate-500 mb-2">Motivo</h4>
                    <p className="text-sm">{selectedMovimiento.motivo}</p>
                  </div>
                )}

                {/* Justificación (para tardanzas/ausencias) */}
                {selectedMovimiento.justificacion && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <h4 className="font-medium text-sm text-amber-600 dark:text-amber-400 mb-2">Justificación</h4>
                    <p className="text-sm">{selectedMovimiento.justificacion}</p>
                  </div>
                )}

                {/* Información de aprobación */}
                {selectedMovimiento.aprobado_por_nombre && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="font-medium text-sm text-green-600 dark:text-green-400 mb-2">Información de Procesamiento</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Procesado por:</span>
                        <p className="font-medium">{selectedMovimiento.aprobado_por_nombre}</p>
                      </div>
                      {selectedMovimiento.fecha_aprobacion && (
                        <div>
                          <span className="text-slate-500">Fecha de Procesamiento:</span>
                          <p className="font-medium">{new Date(selectedMovimiento.fecha_aprobacion).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Metadatos */}
                <div className="pt-3 border-t dark:border-slate-600 text-xs text-slate-500">
                  <p>Creado: {new Date(selectedMovimiento.created_at).toLocaleString()}</p>
                  <p>ID: {selectedMovimiento.id}</p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirmar Eliminación Masiva
              </DialogTitle>
              <DialogDescription>
                Está a punto de eliminar <strong>{selectedIds.length}</strong> movimientos de forma permanente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      Esta acción es IRREVERSIBLE
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Los movimientos seleccionados serán eliminados permanentemente.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-password">Ingrese su contraseña para confirmar</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && handleBulkDelete()}
                  data-testid="delete-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword("");
                }}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleting || !deletePassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedIds.length} movimientos
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
